import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getPreventivi, deletePreventivo } from '../services/api';
import { Table, Form, Spinner, Alert, Row, Col, Dropdown, Pagination, Card, Button, ButtonGroup, InputGroup, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import MobileListLayout from '../common/MobileListLayout';
import StandardPageHeader from '../common/StandardPageHeader';
import ConfirmationModal from '../components/ConfirmationModal';
import Select from 'react-select';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import ColumnOrderController from '../common/ColumnOrderController';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faFileAlt, 
  faCheckCircle,
  faClock,
  faTimesCircle,
  faUser,
  faCalendar,
  faEuroSign,
  faHashtag,
  faSort,
  faSortUp,
  faSortDown,
  faTimes,
  faPlus,
  faCog,
  faLink,
  faEdit,
  faTrash,
  faEye,
  faGripVertical,
  faHourglassHalf
} from '@fortawesome/free-solid-svg-icons';
import SortableHeader from '../common/SortableHeader';

const columnHelper = createColumnHelper();

const FILTER_VARIANTS = {
  stato: 'select',
  data_creazione: 'date-range',
};

const allColumnsMap = {
    id: "ID",
    data_creazione: "Data Creazione",
    cliente_display: "Cliente",
    totale: "Totale",
    stato: "Stato",
    token_accesso: "Link Pubblico",
    actions: "Azioni",
};

const initialVisibleColumns = ['id', 'data_creazione', 'cliente_display', 'totale', 'stato', 'token_accesso', 'actions'];

const visibilityKey = 'table-visibility-preventivi';



function PreventiviList() {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const [preventivi, setPreventivi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [preventivoToDelete, setPreventivoToDelete] = useState(null);
  const [distinctValues, setDistinctValues] = useState({
    stato: [],
  });
  
  // Mantiene tutte le opzioni originali per i filtri multiselect
  const [allDistinctValues, setAllDistinctValues] = useState({
    stato: ['Richiesto', 'In Bozza', 'Inviato', 'Accettato', 'Rifiutato', 'Convertito in Ordine', 'Scaduto'],
  });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem(visibilityKey);
    if (saved) {
      return JSON.parse(saved);
    }
    return Object.keys(allColumnsMap).reduce((acc, key) => {
      acc[key] = initialVisibleColumns.includes(key);
      return acc;
    }, {});
  });
  const [sorting, setSorting] = useState(() => {
    const saved = sessionStorage.getItem('list-sorting-preventivi');
    return saved ? JSON.parse(saved) : [{ id: 'id', desc: true }];
  });

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = sessionStorage.getItem('list-filters-preventivi');
    return saved ? JSON.parse(saved) : [];
  });

  const [quickSearch, setQuickSearch] = useState(() => sessionStorage.getItem('list-quicksearch-preventivi') || '');

  const [columnOrder, setColumnOrder] = useState(() => {
    const savedOrder = localStorage.getItem('table-order-preventivi');
    const defaultOrder = Object.keys(allColumnsMap);
    if (savedOrder) {
        let parsedOrder = JSON.parse(savedOrder);
        return parsedOrder;
    }
    return defaultOrder;
  });
  const [columnSizing, setColumnSizing] = useState(() => {
    const saved = localStorage.getItem('table-sizing-preventivi');
    return saved ? JSON.parse(saved) : {};
  });
  const [quickFilterStatus, setQuickFilterStatus] = useState(null);
  const [isCondensed, setIsCondensed] = useState(() => {
    const saved = localStorage.getItem('table-condensed-preventivi');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    sessionStorage.setItem('list-sorting-preventivi', JSON.stringify(sorting));
  }, [sorting]);

  useEffect(() => {
    sessionStorage.setItem('list-filters-preventivi', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    sessionStorage.setItem('list-quicksearch-preventivi', quickSearch);
    const timer = setTimeout(() => {
      setColumnFilters(prev => {
        const without = prev.filter(f => f.id !== '_global_search');
        return quickSearch ? [...without, { id: '_global_search', value: quickSearch }] : without;
      });
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [quickSearch]);

  useEffect(() => {
    localStorage.setItem('table-order-preventivi', JSON.stringify(columnOrder));
  }, [columnOrder]);

  useEffect(() => {
    localStorage.setItem('table-sizing-preventivi', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalPreventivi, setTotalPreventivi] = useState(0);

  const fetchPreventiviData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const currentFilters = columnFilters.reduce((acc, filter) => {
        const { id, value } = filter;
        
        if (id === '_global_search') {
          // Il backend (get_preventivi) si aspetta la chiave '_global_search', non 'search':
          // prima di questo fix la ricerca rapida veniva silenziosamente ignorata.
          if (value) acc['_global_search'] = value;
          return acc;
        }
        // Date-range filter (oggetto {from, to} prodotto da Filter.js per filterVariant 'date-range')
        if (value && typeof value === 'object' && ('from' in value || 'to' in value)) {
          if (value.from) acc[`${id}_min`] = value.from;
          if (value.to) acc[`${id}_max`] = value.to;
          return acc;
        }
        // Multi-select filter — controlla prima dei range per evitare conflitti
        if (Array.isArray(value) && ['stato'].includes(id)) {
          if (value.length > 0) {
            acc[id] = { 'in': value };
          }
        }
        // Range filter for numbers (array with [min, max])
        else if (Array.isArray(value) && value.length === 2 && (value[0] || value[1])) {
          const [min, max] = value;
          
          // Mapping per range numerici al formato backend (_min/_max)
          const rangeFieldMap = {
            'totale': 'totale',
            'id': 'id',
            'data_creazione': 'data_creazione',
          };
          
          const baseField = rangeFieldMap[id] || id;
          
          if (min) {
            acc[`${baseField}_min`] = parseFloat(min);
          }
          if (max) {
            acc[`${baseField}_max`] = parseFloat(max);
          }
        }
        // Multi-select filter - gestisce oggetti {in: [...]} (legacy)
        else if (value && typeof value === 'object' && 'in' in value && Array.isArray(value.in)) {
          if (value.in.length > 0) {
            acc[id] = value; // Usa direttamente l'oggetto {in: [...]}
          }
        }
        else if (typeof value === 'string' && value.toUpperCase() === 'N/A') {
          acc[id] = null;
        } else if (value !== undefined && value !== '') {
          acc[id] = value;
        }
        return acc;
      }, {});

      // Applica il filtro rapido se presente
      if (quickFilterStatus) {
        currentFilters.stato = quickFilterStatus;
      }

      const filters = JSON.stringify(currentFilters);
      let sortParams = sorting.map(sort => `${sort.id}:${sort.desc ? 'desc' : 'asc'}`);

      // Se non ci sono ordinamenti impostati dall'utente, aggiungi l'ordinamento di default per ID decrescente
      if (sortParams.length === 0) {
        sortParams = ['id:desc'];
      }

      const response = await getPreventivi(
        (currentPage - 1) * itemsPerPage,
        itemsPerPage,
        sortParams,
        currentFilters
      );
      setPreventivi(response.data.preventivi);
      setTotalPreventivi(response.data.total);
      
      if (response.data.facets) {
        setDistinctValues(response.data.facets);
        
        // Per i multiselect, mantieni tutte le opzioni originali
        setAllDistinctValues(prev => {
          const multiselectColumns = ['stato'];
          const updated = { ...prev };
          
          multiselectColumns.forEach(col => {
            if (response.data.facets[col]) {
              // Combina opzioni esistenti con nuove (union)
              const existing = prev[col] || [];
              const newValues = response.data.facets[col] || [];
              updated[col] = [...new Set([...existing, ...newValues])];
            }
          });
          
          return updated;
        });
      }
    } catch (err) {
      setError('Errore nel caricamento dei preventivi.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sorting, columnFilters, quickFilterStatus]);

  useEffect(() => {
    fetchPreventiviData();
  }, [fetchPreventiviData]);

  // Helper functions for icons and badges
  const getStatoBadge = (stato) => {
    const statiConfig = {
      'Richiesto': { variant: 'info', icon: faClock },
      'In Bozza': { variant: 'secondary', icon: null },
      'Inviato': { variant: 'primary', icon: null },
      'Accettato': { variant: 'success', icon: faCheckCircle },
      'Rifiutato': { variant: 'danger', icon: faTimesCircle },
      'Convertito in Ordine': { variant: 'success', icon: faCheckCircle },
      'Scaduto': { variant: 'warning', icon: null }
    };
    const config = statiConfig[stato] || { variant: 'secondary', icon: null };
    return (
      <Badge bg={config.variant} className="d-flex align-items-center gap-1">
        {config.icon && <FontAwesomeIcon icon={config.icon} size="xs" />}
        {stato}
      </Badge>
    );
  };

  const formatCurrency = (value) => {
    if (value == null) return 'N/A';
    const num = parseFloat(value);
    const formatted = num.toFixed(2);
    return <span className="text-success">€ {formatted}</span>;
  };

  const getColumnHeaderWithIcon = (columnKey, columnLabel) => {
    const iconMap = {
      'id': faHashtag,
      'data_creazione': faCalendar,
      'cliente_display': faUser,
      'totale': faEuroSign,
      'stato': faCheckCircle,
      'token_accesso': faLink,
    };
    
    const icon = iconMap[columnKey];
    if (icon) {
      return (
        <>
          <FontAwesomeIcon icon={icon} className="me-1" />
          {columnLabel}
        </>
      );
    }
    return columnLabel;
  };

  const getColumnValue = (preventivo, columnName) => {
    switch (columnName) {
      case 'cliente_display':
        if (!preventivo.cliente && !preventivo.cliente_nome) return 'N/A';
        if (preventivo.cliente_nome) return preventivo.cliente_nome;
        if (preventivo.cliente) {
          const { nome, cognome, denominazione_sociale } = preventivo.cliente;
          const namePart = [nome, cognome].filter(Boolean).join(' ');
          if (denominazione_sociale) {
            return namePart ? `${namePart} (${denominazione_sociale})` : denominazione_sociale;
          }
          return namePart || 'Cliente non specificato';
        }
        return 'N/A';
      case 'id':
        return preventivo.numero_completo || preventivo.id;
      case 'data_creazione':
        return preventivo.data_creazione ? new Date(preventivo.data_creazione).toLocaleDateString() : 'N/A';
      case 'stato':
        return getStatoBadge(preventivo.stato);
      case 'totale':
        return preventivo.totale != null ? formatCurrency(preventivo.totale) : 'N/A';
      case 'token_accesso':
        return preventivo.token_accesso ? (
          <a 
            href={`/preventivo/${preventivo.token_accesso}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-info"
            onClick={(e) => e.stopPropagation()}
          >
            <FontAwesomeIcon icon={faLink} /> Visualizza
          </a>
        ) : 'N/A';
      case 'actions':
        return (
          <div className="d-flex gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              as={Link}
              to={`/preventivi/${preventivo.id}`}
              variant="info"
              size="sm"
            >
              <FontAwesomeIcon icon={faEye} />
            </Button>
            <Button
              as={Link}
              to={`/preventivi/edit/${preventivo.id}`}
              variant="warning"
              size="sm"
            >
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDeleteClick(preventivo)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </div>
        );
      default:
        return preventivo[columnName];
    }
  };

  const columns = useMemo(
    () => [
      ...Object.entries(allColumnsMap).map(([key, value]) =>
        columnHelper.accessor(key, {
          header: () => getColumnHeaderWithIcon(key, value),
          cell: info => getColumnValue(info.row.original, key),
          enableColumnFilter: key !== 'actions' && key !== 'token_accesso',
          enableSorting: key !== 'actions' && key !== 'token_accesso',
          meta: {
            filterVariant: FILTER_VARIANTS[key] || 'text',
            filterOptions: key === 'stato' ? allDistinctValues[key] : distinctValues[key],
          },
        })
      ),
    ],
    [preventivi, distinctValues, allDistinctValues]
  );

  const onColumnVisibilityChange = (updater) => {
    const newState = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newState);
    localStorage.setItem(visibilityKey, JSON.stringify(newState));
  };

  const table = useReactTable({
    data: preventivi,
    columns,
    state: {
      columnVisibility,
      sorting,
      columnFilters,
      columnOrder,
      columnSizing,
      rowSelection,
    },
    onColumnVisibilityChange: onColumnVisibilityChange,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    enableRowSelection: false,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    manualFiltering: true,
    manualSorting: true,
  });

  const handleAutoResize = (column) => {
    // Define optimal widths for each column type
    const columnOptimalWidths = {
      'id': 80,
      'data_creazione': 130,
      'cliente_display': 200,
      'totale': 100,
      'stato': 150,
      'token_accesso': 150,
      'actions': 150,
    };
    
    const optimalWidth = columnOptimalWidths[column.id] || 120;

    table.setColumnSizing(prev => ({
      ...prev,
      [column.id]: optimalWidth,
    }));
  };

  const handleDeleteClick = (preventivo) => {
    setPreventivoToDelete(preventivo);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (preventivoToDelete) {
      try {
        await deletePreventivo(preventivoToDelete.id);
        fetchPreventiviData();
      } catch (error) {
        console.error('Errore durante l\'eliminazione del preventivo:', error);
        setError('Errore durante l\'eliminazione del preventivo.');
      } finally {
        setShowDeleteModal(false);
        setPreventivoToDelete(null);
      }
    }
  };

  const handleQuickFilterChange = (status) => {
    // Se il filtro rapido cliccato è già attivo, disattivalo
    if (quickFilterStatus === status) {
      setQuickFilterStatus(null);
    } else {
      setQuickFilterStatus(status);
    }
    setCurrentPage(1); // Reset pagina quando si cambia filtro
  };

  const handleResetFilters = () => {
    table.resetColumnFilters();
    setSorting([]);
    setQuickFilterStatus(null);
    setQuickSearch('');
    setCurrentPage(1);
  };

  // Funzione per renderizzare la card mobile di un preventivo
  const renderPreventivoCard = useCallback((preventivo) => {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            <h6 className="mb-1">
              <FontAwesomeIcon icon={faFileAlt} className="me-2 text-muted" />
              Preventivo {preventivo.numero_completo || `#${preventivo.id}`}
            </h6>
            <small className="text-muted">
              <FontAwesomeIcon icon={faCalendar} className="me-1" />
              {new Date(preventivo.data_creazione).toLocaleDateString('it-IT')}
            </small>
          </div>
          {getStatoBadge(preventivo.stato)}
        </div>
        <div className="mb-2">
          <FontAwesomeIcon icon={faUser} className="me-2 text-muted" />
          <strong>{getColumnValue(preventivo, 'cliente_display')}</strong>
        </div>
        <Row className="mb-2">
          <Col xs={6}>
            <small className="text-muted d-block">Totale</small>
            <strong style={{ fontSize: '1.1rem', color: '#0d6efd' }}>
              {formatCurrency(preventivo.totale)}
            </strong>
          </Col>
          <Col xs={6}>
            {preventivo.token_accesso && (
              <Button
                as="a"
                href={`/preventivo/${preventivo.token_accesso}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="info"
                size="sm"
                className="w-100"
              >
                <FontAwesomeIcon icon={faLink} /> Link Pubblico
              </Button>
            )}
          </Col>
        </Row>
        <div className="d-flex gap-2 mt-3">
          <Button
            as={Link}
            to={`/preventivi/${preventivo.id}`}
            variant="info"
            size="sm"
            className="flex-fill"
          >
            <FontAwesomeIcon icon={faEye} className="me-1" />
            Visualizza
          </Button>
          <Button
            as={Link}
            to={`/preventivi/edit/${preventivo.id}`}
            variant="warning"
            size="sm"
            className="flex-fill"
          >
            <FontAwesomeIcon icon={faEdit} className="me-1" />
            Modifica
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => handleDeleteClick(preventivo)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      </div>
    );
  }, []);

  // Quick filters per mobile
  const quickFilters = useMemo(() => [
    {
      label: quickFilterStatus === 'In Bozza' ? 'Tutti' : 'In Bozza',
      active: quickFilterStatus === 'In Bozza',
      onClick: () => handleQuickFilterChange('In Bozza'),
      variant: 'secondary'
    },
    {
      label: quickFilterStatus === 'Inviato' ? 'Tutti' : 'Inviato',
      active: quickFilterStatus === 'Inviato',
      onClick: () => handleQuickFilterChange('Inviato'),
      variant: 'primary'
    },
    {
      label: quickFilterStatus === 'Accettato' ? 'Tutti' : 'Accettato',
      active: quickFilterStatus === 'Accettato',
      onClick: () => handleQuickFilterChange('Accettato'),
      variant: 'success'
    }
  ], [quickFilterStatus]);

  // Calcola il numero di filtri attivi
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (columnFilters.length > 0) count += columnFilters.length;
    if (quickFilterStatus) count += 1;
    return count;
  }, [columnFilters, quickFilterStatus]);

  const hasManualFilters = activeFiltersCount > 0 || sorting.length > 0;

  const toggleCondensedMode = () => {
    const newValue = !isCondensed;
    setIsCondensed(newValue);
    localStorage.setItem('table-condensed-preventivi', JSON.stringify(newValue));
  };

  const handleAutoFitAllColumns = () => {
    const newSizing = {};
    
    // Get viewport width and reserve space for sticky columns and margins
    const viewportWidth = window.innerWidth;
    const reservedSpace = 100;
    const availableWidth = viewportWidth - reservedSpace;
    
    // Get visible columns
    const visibleColumns = table.getAllLeafColumns().filter(col => 
      col.getIsVisible()
    );
    
    // Define optimal widths for each column type
    const columnOptimalWidths = {
      'id': 80,
      'data_creazione': 130,
      'cliente_display': 200,
      'totale': 100,
      'stato': 150,
      'token_accesso': 150,
      'actions': 150,
    };
    
    // Calculate total optimal width
    let totalOptimalWidth = 0;
    visibleColumns.forEach(column => {
      const optimalWidth = columnOptimalWidths[column.id] || 120;
      totalOptimalWidth += optimalWidth;
    });
    
    // If total optimal width is less than available, use optimal widths
    // Otherwise, scale proportionally but with minimum constraints
    if (totalOptimalWidth <= availableWidth) {
      visibleColumns.forEach(column => {
        newSizing[column.id] = columnOptimalWidths[column.id] || 120;
      });
    } else {
      // Scale down but maintain minimum widths
      const scaleFactor = availableWidth / totalOptimalWidth;
      
      visibleColumns.forEach(column => {
        const optimalWidth = columnOptimalWidths[column.id] || 120;
        const scaledWidth = Math.floor(optimalWidth * scaleFactor);
        
        // Minimum widths by column type
        const minWidths = {
          'id': 70,
          'data_creazione': 100,
          'cliente_display': 150,
          'totale': 80,
          'stato': 100,
          'default': 90
        };
        
        const minWidth = minWidths[column.id] || minWidths.default;
        newSizing[column.id] = Math.max(minWidth, scaledWidth);
      });
    }
    
    setColumnSizing(newSizing);
  };

  const handleResetColumnWidths = () => {
    setColumnSizing({});
  };

  const renderPaginationItems = () => {
    const totalPages = Math.ceil(totalPreventivi / itemsPerPage);
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage, endPage;

    if (totalPages <= maxPagesToShow) {
        startPage = 1;
        endPage = totalPages;
    } else {
        if (currentPage <= Math.ceil(maxPagesToShow / 2)) {
            startPage = 1;
            endPage = maxPagesToShow;
        } else if (currentPage + Math.floor(maxPagesToShow / 2) >= totalPages) {
            startPage = totalPages - maxPagesToShow + 1;
            endPage = totalPages;
        } else {
            startPage = currentPage - Math.floor(maxPagesToShow / 2);
            endPage = currentPage + Math.floor(maxPagesToShow / 2);
        }
    }

    for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(
            <Pagination.Item key={i} active={i === currentPage} onClick={() => setCurrentPage(i)}>
                {i}
            </Pagination.Item>
        );
    }
    return pageNumbers;
  };

  // Calculate stats
  const statsAccettati = preventivi.filter(p => p.stato === 'Accettato').length;
  const statsInAttesa = preventivi.filter(p => p.stato === 'Inviato' || p.stato === 'In Bozza' || p.stato === 'Richiesto').length;
  const statsRifiutati = preventivi.filter(p => p.stato === 'Rifiutato').length;

  return (
    <>
      <StandardPageHeader 
        title="Preventivi"
        stats={[
          { label: 'Totale preventivi', value: totalPreventivi, icon: faFileAlt, color: 'primary' },
          { label: 'Accettati', value: statsAccettati, icon: faCheckCircle, color: 'success' },
          { label: 'In attesa', value: statsInAttesa, icon: faClock, color: 'warning' },
          { label: 'Rifiutati', value: statsRifiutati, icon: faTimesCircle, color: 'danger' }
        ]}
      />
      
    <Card style={{ marginBottom: '80px' }}>
      <Card.Header className="sticky-card-header">
        {isMobile ? (
          // Mobile: Layout semplificato
          <div className="mobile-toolbar p-3 bg-light rounded-3 shadow-sm">
            <Row className="g-2 mb-3">
              <Col xs={7}>
                <Button 
                  as={Link} 
                  to="/preventivi/new" 
                  variant="success" 
                  className="w-100 fw-bold shadow-sm border-0"
                  style={{ 
                    background: 'linear-gradient(45deg, #28a745, #20c997)',
                    borderRadius: '12px',
                    padding: '10px'
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Nuovo Preventivo
                </Button>
              </Col>
              <Col xs={5}>
                <Button
                  variant={isCondensed ? 'primary' : 'outline-primary'}
                  className="w-100 shadow-sm border-0"
                  style={{ borderRadius: '12px', padding: '10px' }}
                  onClick={toggleCondensedMode}
                >
                  <FontAwesomeIcon icon={faCog} className="me-1" />
                  {isCondensed ? 'Compatto' : 'Normale'}
                </Button>
              </Col>
            </Row>
            
            <Row className="g-2 mb-3">
              <Col xs={4}>
                <Button
                  variant={quickFilterStatus === 'Inviato' ? 'primary' : 'outline-primary'}
                  size="sm"
                  onClick={() => handleQuickFilterChange('Inviato')}
                  className="w-100 shadow-sm border-0"
                  style={{ 
                    borderRadius: '10px',
                    fontWeight: '600',
                    padding: '8px 4px'
                  }}
                >
                  <FontAwesomeIcon icon={faHourglassHalf} className="me-1" />
                  <div className="small">Inviato</div>
                </Button>
              </Col>
              <Col xs={4}>
                <Button
                  variant={quickFilterStatus === 'Accettato' ? 'success' : 'outline-success'}
                  size="sm"
                  onClick={() => handleQuickFilterChange('Accettato')}
                  className="w-100 shadow-sm border-0"
                  style={{ 
                    borderRadius: '10px',
                    fontWeight: '600',
                    padding: '8px 4px'
                  }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
                  <div className="small">Accettato</div>
                </Button>
              </Col>
              <Col xs={4}>
                <Button
                  variant={quickFilterStatus === 'Rifiutato' ? 'danger' : 'outline-danger'}
                  size="sm"
                  onClick={() => handleQuickFilterChange('Rifiutato')}
                  className="w-100 shadow-sm border-0"
                  style={{ 
                    borderRadius: '10px',
                    fontWeight: '600',
                    padding: '8px 4px'
                  }}
                >
                  <FontAwesomeIcon icon={faTimesCircle} className="me-1" />
                  <div className="small">Rifiutato</div>
                </Button>
              </Col>
            </Row>
            
            <Row>
              <Col>
                <InputGroup className="shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                  <InputGroup.Text className="bg-white border-0" style={{ borderRadius: '12px 0 0 12px' }}>
                    <FontAwesomeIcon icon={faSearch} className="text-primary" />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Cerca preventivi..."
                    onChange={() => {}}
                    className="border-0"
                    style={{ background: '#fff' }}
                  />
                  <Button
                      variant="outline-secondary"
                      onClick={handleResetFilters}
                      disabled={!hasManualFilters}
                      className="border-0 bg-white"
                      style={{ borderRadius: '0 12px 12px 0' }}
                  >
                      <FontAwesomeIcon icon={faTimes} className={!hasManualFilters ? 'text-muted' : 'text-danger'} />
                      {activeFiltersCount > 0 && (
                        <Badge bg="danger" className="ms-1">{activeFiltersCount}</Badge>
                      )}
                  </Button>
                </InputGroup>
              </Col>
            </Row>
          </div>
        ) : (
          // Desktop: Layout originale
          <Row className="align-items-center">
          <Col md={5} className="d-flex justify-content-start align-items-center flex-wrap gap-2">
            <Button as={Link} to="/preventivi/new" size="sm" variant="success">
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Nuovo Preventivo
            </Button>
            
            <ButtonGroup size="sm">
              <Button
                variant={quickFilterStatus === 'Inviato' ? 'primary' : 'outline-secondary'}
                onClick={() => handleQuickFilterChange('Inviato')}
              >
                Inviato
              </Button>
              <Button
                variant={quickFilterStatus === 'Accettato' ? 'primary' : 'outline-secondary'}
                onClick={() => handleQuickFilterChange('Accettato')}
              >
                Accettato
              </Button>
              <Button
                variant={quickFilterStatus === 'Rifiutato' ? 'primary' : 'outline-secondary'}
                onClick={() => handleQuickFilterChange('Rifiutato')}
              >
                Rifiutato
              </Button>
            </ButtonGroup>
          </Col>
          

          <Col md={4} className="d-flex justify-content-end align-items-center flex-wrap gap-2">
            <Dropdown autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-actions" size="sm">
                Azioni
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleAutoFitAllColumns}>
                  ⚡ Auto-Fit Colonne
                </Dropdown.Item>
                <Dropdown.Item onClick={handleResetColumnWidths}>
                  🔄 Reset Colonne
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns" size="sm">
                Colonne
              </Dropdown.Toggle>
              <Dropdown.Menu className="long-dropdown-menu p-0" style={{ minWidth: '500px' }}>
                <div className="d-flex">
                  <div className="p-2" style={{ flex: 1, borderRight: '1px solid #dee2e6' }}>
                    <ColumnOrderController
                      table={table}
                      allColumnsMap={allColumnsMap}
                    />
                  </div>
                  <div className="p-2" style={{ flex: 1 }}>
                    <h6 className="fw-bold">Mostra colonne</h6>
                    <div className="d-flex flex-column" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                      {table.getAllColumns().map(column => (
                        <Form.Check
                          key={column.id}
                          type="checkbox"
                          label={allColumnsMap[column.id]}
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                          className="dropdown-item"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
            
            <Button
              variant={isCondensed ? 'primary' : 'outline-secondary'}
              size="sm"
              onClick={toggleCondensedMode}
              title="Modalità condensata"
            >
              {isCondensed ? '📋 Compatto' : '📋 Normale'}
            </Button>
            
            <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleResetFilters}
                disabled={!hasManualFilters}
            >
                Azzera filtri
                {activeFiltersCount > 0 && (
                  <Badge bg="danger" className="ms-2">{activeFiltersCount}</Badge>
                )}
            </Button>
          </Col>

          </Row>
        )}
      </Card.Header>
      <Card.Body>
        <Form.Control
          type="text"
          className="mb-3"
          placeholder="Cerca per cliente, email..."
          value={quickSearch}
          onChange={e => setQuickSearch(e.target.value)}
        />
        {loading && <div className="text-center"><Spinner animation="border" /></div>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!loading && !error && (
          <div>
            {isMobile ? (
              <MobileListLayout
                items={preventivi}
                renderCard={renderPreventivoCard}
                entityName="preventivo"
                createPath="/preventivi/nuovo"
                quickFilters={quickFilters}
                showSelectAll={false}
              />
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
                <Table 
                  striped 
                  bordered 
                  hover 
                  style={{ width: table.getTotalSize() }} 
                  className={`aligned-table enhanced-table ${isCondensed ? 'table-condensed' : ''}`}
                >
                <thead>
                  {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    <tr>
                      {headerGroup.headers.map(header => (
                        <SortableHeader key={header.id} header={header} table={table} handleAutoResize={handleAutoResize} stickyColumns={['select', 'actions']} />
                      ))}
                    </tr>

                  </React.Fragment>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center">Nessun preventivo trovato.</td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map(row => (
                      <tr key={row.id} style={{ cursor: 'default' }}>
                        {row.getVisibleCells().map(cell => {
                          return (
                            <td 
                              key={cell.id}
                              style={{ 
                                verticalAlign: 'middle', 
                                width: cell.column.getSize(), 
                                minWidth: cell.column.getSize(),
                                maxWidth: cell.column.getSize(),
                                overflow: 'hidden', 
                                textOverflow: 'ellipsis', 
                                whiteSpace: 'nowrap' 
                              }}
                              title={typeof cell.getValue() === 'string' ? cell.getValue() : ''}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              </div>
            )}
          </div>
        )}
      </Card.Body>
      {!isMobile && (
        <Card.Footer className="d-flex justify-content-between align-items-center">
          <Form.Group as={Row} className="align-items-center mb-0">
            <Col xs="auto">
              <Form.Label className="mb-0">Record per pagina</Form.Label>
            </Col>
            <Col xs="auto">
              <Form.Select
                size="sm"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Form.Select>
            </Col>
          </Form.Group>
          <Pagination size="sm" className="mb-0">
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(prev => prev - 1)} disabled={currentPage === 1} />
            {renderPaginationItems()}
            <Pagination.Next onClick={() => setCurrentPage(prev => prev + 1)} disabled={currentPage === Math.ceil(totalPreventivi / itemsPerPage)} />
            <Pagination.Last onClick={() => setCurrentPage(Math.ceil(totalPreventivi / itemsPerPage))} disabled={currentPage === Math.ceil(totalPreventivi / itemsPerPage)} />
          </Pagination>
        </Card.Footer>
      )}

      {/* Stili CSS per react-select */}
      <style jsx>{`
        .react-select-container .react-select__control {
          border-radius: 0.375rem;
          border: 1px solid #ced4da;
          box-shadow: none;
          min-height: 31px;
        }
        
        .react-select-container .react-select__control--is-focused {
          border-color: #86b7fe;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .react-select-container .react-select__multi-value {
          background-color: #e9ecef;
          border-radius: 0.25rem;
        }
        
        .react-select-container .react-select__multi-value__label {
          color: #495057;
          font-size: 0.75rem;
          padding: 0.125rem 0.25rem;
        }
        
        .react-select-container .react-select__multi-value__remove {
          color: #6c757d;
          border-radius: 0 0.25rem 0.25rem 0;
        }
        
        .react-select-container .react-select__multi-value__remove:hover {
          background-color: #dc3545;
          color: white;
        }
        
        .react-select-container .react-select__placeholder {
          color: #6c757d;
          font-size: 0.75rem;
        }
        
        .react-select-container .react-select__input {
          font-size: 0.75rem;
        }
        
        .react-select-container .react-select__menu {
          z-index: 1050;
        }

        .enhanced-table .resizer {
          position: absolute;
          right: 0;
          top: 0;
          height: 100%;
          width: 5px;
          background: rgba(0, 0, 0, 0.1);
          cursor: col-resize;
          user-select: none;
          touch-action: none;
        }

        .enhanced-table .resizer.isResizing {
          background: blue;
          opacity: 1;
        }

        .enhanced-table .resizer:hover {
          background: rgba(0, 123, 255, 0.5);
        }

        .enhanced-table th {
          position: relative;
        }

        .table-condensed td,
        .table-condensed th {
          padding: 0.25rem !important;
          font-size: 0.85rem !important;
        }
      `}</style>
    </Card>

    <ConfirmationModal
      show={showDeleteModal}
      title="Conferma Eliminazione"
      body={`Sei sicuro di voler eliminare il preventivo #${preventivoToDelete?.id}?`}
      onConfirm={handleDeleteConfirm}
      onCancel={() => setShowDeleteModal(false)}
    />
    </>
  );
}

export default PreventiviList;
